import { ChangeDetectorRef, Component, Inject, PLATFORM_ID, ViewChild } from "@angular/core";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { MatPaginator, MatPaginatorModule } from "@angular/material/paginator";
import { MatSort, MatSortModule } from "@angular/material/sort";
import { RouterModule } from "@angular/router";
import { CommonModule, isPlatformBrowser } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { allColumns } from "./column-groups";
import { BehaviorSubject, map, Observable, Subject, takeUntil } from "rxjs";
import { TeamListDto } from "../../../../core/models/teams/team-list-dto";
import { TeamListFilter } from "../../../../core/models/teams/team-list-filter";
import { TeamListService } from "../../../../core/services/team-list/team-list.service";
import { TeamListFilterComponent } from "../../../../core/components/team-list-filter/team-list-filter.component";
import { ColumnManagerService } from "../../../../core/services/column-manager/column-manager.service";
import { MatButtonModule } from "@angular/material/button";
import { ColumnDefinition, ColumnDefinitionTeamList, GenericCellType } from "../../../../core/models/column-definition";
import { FilterItem } from "../../../../core/models/filter-item";
import { MatDialog } from "@angular/material/dialog";
import { AuthService } from "../../../../core/services/auth.service";
import * as XLSX from 'xlsx';
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { IconService } from "../../../../core/services/icon.service";
import { NavigationService } from "../../../../core/services/navigation.service";
import { SortCoordinationService } from "../../../../core/services/sort-coordination/sort-coordination.service";
import { MatMenuModule } from "@angular/material/menu";

@Component({
  selector: 'app-team-list',
  imports: [MatTableModule, MatPaginatorModule, MatSortModule, RouterModule, CommonModule, MatIconModule, TeamListFilterComponent, MatButtonModule, FaIconComponent, MatMenuModule],
  templateUrl: './team-list.component.html',
  styleUrl: './team-list.component.scss'
})
export class TeamListComponent {
  private readonly ngUnsubscribe$ = new Subject<void>();
  private allColumns: ColumnDefinition[] = allColumns;
  private readonly tableId = 'team-table';

  teams: TeamListDto[] = [];
  protected filteredTeamList$: Observable<MatTableDataSource<TeamListDto>> | undefined;
  teamDataSource = new MatTableDataSource<TeamListDto>;

  isLoading = true;
  displayedColumns: (string | undefined)[] = [];
  skeletonRows = Array(20).fill(0);


  data$!: BehaviorSubject<TeamListDto[] | null>;
  @ViewChild(MatPaginator) paginator: MatPaginator | undefined;
  @ViewChild(MatSort) sort: MatSort | undefined;

  combinedColumns: ColumnDefinitionTeamList[] = [];
  isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) private platformId: Object,
    private dialog: MatDialog,
    private teamListService: TeamListService,
    private columnManagerService: ColumnManagerService,
    private changeDetectorRef: ChangeDetectorRef,
    public authService: AuthService,
    private navigationService: NavigationService,
    public iconService: IconService,
    private sortCoordinationService: SortCoordinationService) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnDestroy() {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private initSubscriptions = (): void => {
    this.filteredTeamList$ = this.teamListService.filterChange.pipe(takeUntil(this.ngUnsubscribe$))
      .pipe(
        map((value) => {
          return this.applyFilter(
            value || ""
          );
        })
      );
  }

  async ngOnInit(): Promise<void> {
    if (!this.isBrowser) {
      // Avoid backend calls during SSR/prerender.
      this.isLoading = false;
      return;
    }

    this.columnManagerService.registerTable(this.tableId, this.allColumns);

    this.columnManagerService.loadFromStorage(this.tableId);

    this.displayedColumns = this.columnManagerService.getDisplayedColumns(this.tableId);
    this.combinedColumns = this.allColumns.filter(cc => this.displayedColumns.includes(cc.columnDef)) as ColumnDefinitionTeamList[];

    this.teamListService.loadTeams();
    this.data$ = this.teamListService.data$;
    this.data$.subscribe(teams => {
      if (teams) {
        this.teams = teams;
        this.isLoading = false;
      }
    })
    this.initSubscriptions();

    this.sortCoordinationService.clearSort$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(tableId => {
        if (tableId === this.tableId && this.sort) {
          this.sort.active = '';
          this.sort.direction = '';
          this.columnManagerService.clearSortState(this.tableId);
          this.sort.sortChange.emit({ active: '', direction: '' });
        }
      });
  }

  async ngAfterViewInit(): Promise<void> {
    if (!this.isBrowser) {
      return;
    }

    this.teamListService.filterChange.subscribe(filter => {
      this.applyFilter(filter);
      this.changeDetectorRef.detectChanges();
    });

    this.teamDataSource.paginator = this.paginator!;
    this.teamDataSource.sort = this.sort!;

    const savedPageSize = this.columnManagerService.loadPageSize(this.tableId);
    if (savedPageSize && this.paginator) {
      this.paginator.pageSize = savedPageSize;
    }

    this.paginator?.page.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((pageEvent) => {
      this.columnManagerService.savePageSize(this.tableId, pageEvent.pageSize);
    });

    const savedSort = this.columnManagerService.loadSortState(this.tableId);
    if (savedSort && this.sort) {
      this.sort.active = savedSort.active;
      this.sort.direction = savedSort.direction;
      this.sort.sortChange.emit({ active: savedSort.active, direction: savedSort.direction });
    }

    this.sort?.sortChange.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(() => {
      if (this.sort) {
        this.columnManagerService.saveSortState(this.tableId, this.sort);
      }
    });

    this.teamDataSource.sortingDataAccessor = (item: any, property: string): any => {
      return property.split('.').reduce((obj, key) => obj?.[key], item);
    };
  }

  openColumnManager() {
    this.columnManagerService.openColumnManager(this.tableId)
      .subscribe(result => {
        if (result) {

          this.displayedColumns = result.displayedColumns;
          this.combinedColumns = this.allColumns.filter(cc => this.displayedColumns.includes(cc.columnDef)) as ColumnDefinitionTeamList[];


          this.columnManagerService.saveToStorage(this.tableId);
        }
      });
  }

  applyFilter(filterValues: TeamListFilter) {
    let teams = this.data$.value!;

    if (!teams) {
      return this.teamDataSource;
    }

    if (filterValues?.productionTechnicianArea.length) {
      teams = teams.filter((team) => this.isPropertyFiltered(filterValues.productionTechnicianArea, team.productionTechnicianAreaText));
    }
    if (filterValues?.productionLeader.length) {
      teams = teams.filter((team) => this.isPropertyFiltered(filterValues.productionLeader, team.baseInfo.productionLeaderText));
    }

    if (filterValues?.ownerType.length) {
      teams = teams.filter((team) => this.isPropertyFiltered(filterValues.ownerType, team.statusInfo.ownerTypeText));
    }
    if (filterValues?.status.length) {
      teams = teams.filter((team) => this.isPropertyFiltered(filterValues.status, team.statusInfo.statusText));
    }

    const activeSearch = filterValues?.search?.find((item) => item?.activated && !!item?.value?.trim());
    if (activeSearch) {
      teams = teams.filter((team) => this.checkNestedObject(team, activeSearch.value, activeSearch.activated));
    }

    this.teamDataSource.data = teams;

    if (this.paginator) this.teamDataSource.paginator = this.paginator;
    if (this.sort) this.teamDataSource.sort = this.sort;

    return this.teamDataSource;
  }

  isPropertyFiltered(filterItems: FilterItem[], property: string): boolean {
    return filterItems.every(f => !f.activated) || filterItems.some(f => f.value === property && f.activated);
  }

  checkNestedObject = (obj: any, filter: string, activated: boolean = true): boolean => {
    if (!activated) {
      return true;
    }

    if (!filter) {
      return true;
    }

    return Object.keys(obj).some(key => {
      const value = obj[key];
      if (value && typeof value === 'object') {
        return this.checkNestedObject(value, filter);
      }
      const lowerCaseFilter = filter.toLowerCase();
      return value && value.toString().toLowerCase().includes(lowerCaseFilter);
    });
  };


  getProperty = (obj: any, path: any) => (
    path.split('.').reduce((o: any, p: any) => o && o[p], obj)
  )

  getCellValue(value: GenericCellType): GenericCellType {
    const returnedValue = (value !== 'null' && value !== 'undefined') ? value : '-';
    return returnedValue;
  }

  trackByColumn(index: number, column: ColumnDefinition): string | undefined {
    return column.columnDef;
  }

  openCreateTeamDialog() {
    this.navigationService.navigateToTeamById({ id: 0 });

  }

  exportToExcel() {
    let element = document.getElementById('team-list');
    const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(element, { raw: true });

    ws['!cols'] = [{ wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 30 }, { wch: 20 }, { wch: 20 }, { wch: 10 }, { wch: 20 }, { wch: 10 }, { wch: 20 }];
    ws['A1'].v = 'Maskinlagskod';
    ws['B1'].v = 'Produktionsteknikerområde';

    // Formatera alla celler som text för att förhindra datumtolkning
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    for (let row = range.s.r; row <= range.e.r; row++) {
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        const cell = ws[cellAddress];
        if (cell) {
          cell.t = 's';
          cell.z = '@';
        }
      }
    }

    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Maskinlag');

    XLSX.writeFile(wb, 'Maskinlag.xlsx');
  }
}
